/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : AI 검색 관련 탭
 * 버전 : 1.0
 * 파일명 : aiRecommendation.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-08-21, 강동형 : 저작권 추가, AI 관련 API 연결 및 홍보 문구 조건 수정
 * ================================================================ 
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeft';
import { BottomSheetType } from 'src/types/SchoolType';
import { Icon_Loading } from './CommonIcons';
import { getStrategicInfo, postRecommendRoles } from 'src/api/SchoolService';

const AIRecommendationContainer = styled(Box)({
  // backgroundColor: '#f0f0f0', // 연한 회색 배경
  gap: '16px',
  padding: 0, // 패딩 제거
  margin: 0, // 마진 제거
});

const RecommendationBox = styled(Box)<{ centered: boolean }>(({ centered }) => ({
  backgroundColor: '#FFFFFF', // 흰색 배경
  boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.10)',
  borderRadius: '8px',
  minHeight: '168px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: centered ? 'center': 'flex-start',
  alignItems: 'center',
  padding:'16px 24px',
  overflowX:'hidden',
  overflowY: 'hidden', // ✅ Y축 스크롤바 숨김
  gap:'10px',
  height:'auto',
  flexShrink: 0
}));

const NumberCircleKeyword = styled(Box)({
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: 'white',
  color: 'black',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '8px',
  marginBottom: '6px',
  fontWeight: 'bold',
  fontSize:12
});

const NumberCircleStrategy = styled(Box)({
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: 'white',
  color: '#024C61',
  border: '0.87px solid #024C61',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '16px',
  fontWeight: 'bold',
  fontSize:'12px'
});

const TitleContainer = styled('div')({
  display:'flex',
  justifyContent: 'center',
  alignItems:'center',
  // flexDirection:'column'
})

const ContentWrapper = styled('div')<{ fromMain: boolean }>(({ fromMain }) => ({
  padding : '20px',
  display:'flex',
  gap:'30px',
  flexDirection:'column',
  width:'100vw',
  overflowY:'auto',
  height: fromMain ? 'calc(100vh - 116px)' : 'calc(100vh - 206px)',
}))

const AIResultWrapper = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',  // 2열
  gap: '7px',                     // 요소 간 간격
  width: '100%',
  marginTop: '6px',
  justifyItems: 'center',         // 각 셀 안 요소 가운데 정렬
  alignItems: 'center',           // 행 정렬
})

const FadeInDiv = styled('div')<{ delay: number }>(({ delay }) => ({
  width:'100%',
  opacity: 0,
  animation: `fadeIn 0.5s ease forwards`,
  animationDelay: `${delay}s`, // 각각 delay 다르게!
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const KeywordBox = styled(Box)<{ bgColor: string }>(({ bgColor }) => ({
  backgroundColor: bgColor,
  width: '100%',
  height: '60px',
  color: 'black',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow:'hidden',
  flexShrink:1,
}));

const StrategyWrapper = styled('div')({
  // marginTop:'10px',
  display:'flex',
  flexDirection:'column',
  gap:'10px',

  paddingRight: '4px'
})

const SearchPromotionButton = styled('button')({
  color: 'white',
  padding: '10px',
  fontSize: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  width:'210px',
  // height:'42px',
  fontWeight:500,

  borderRadius: '7px',
  border: '2px solid transparent',
  backgroundImage: `
    linear-gradient(270deg, #003E43 0%, #00A9B6 50%, #003E43 100%),
    linear-gradient(270deg, #73C7DF 0%, #FFFFFF 50%, #73C7DF 100%)
  `,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box', // ✅ 보더와 내부 나눠서 클립
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
})

interface AIRecommendationProps {
  selectedSchool: BottomSheetType | null;
  onBackClick: React.Dispatch<React.SetStateAction<boolean>>;
  fromMain?:boolean;
}

// 시드 기반 랜덤 생성기
function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// 배열 섞기 (시드 사용)
function shuffleArray<T>(array: T[], seed: number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// AIRecommendation 컴포넌트 정의
const AIRecommendation = ({ selectedSchool, onBackClick, fromMain }: AIRecommendationProps) => {
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [strategies, setStrategies] = useState<string[]>([]);
  const [displayedStrategies, setDisplayedStrategies] = useState<string[]>([]);

  const handleKeywordSearch = async (seed:number) => {
    setLoadingKeywords(true);
    if(selectedSchool?.school_class.includes('대학교') || selectedSchool?.school_type === '특성화고등학교'){
       try {
        // ✅ 여기서 POST가 끝날 때까지 로딩 애니메이션 유지됨
        const res = await postRecommendRoles(selectedSchool?.school_class, selectedSchool?.school_id);
        
        setKeywords(res.data)
      } catch (e) {
        // (옵션) 에러 처리
        console.error(e);
      } finally {
        setLoadingKeywords(false); // ✅ POST 종료 시 애니메이션 OFF
      }
    }
    else{
              setTimeout(() => {
          const candidates = ['국제행사', '해외파병', '사이버작전', '군통신망', '영상제작', '홍보물디자인', '종교음악', '미술전시','기강유지','진료','심리전수행','작전수행','전투기술','정보수집','제압기술','후송'];
          const shuffled = shuffleArray(candidates, seed); // ✅ 시드 기반으로 섞음
          const picked = shuffled.slice(0, 4); // 4개만 뽑기
          setKeywords(picked);
          setLoadingKeywords(false);
        }, 2000);
    }
  }

  const handleStrategySearch = async (seed:number, is_highschool:boolean) => {
    setLoadingStrategy(true);
    const response = await getStrategicInfo({school_id:seed,is_highschool:is_highschool})
    setTimeout(() => {
      if(response.data.ai_advice.length !== 0){
        setStrategies(response.data.ai_advice);
      }
      else{
        const candidates= ['가족과 여가 시간을 보장받으며 안정적인 삶을 추구할 수 있는 소득 수준과 근무 안정성을 이야기합니다.',
          '다양한 병과와 직무가 체계적으로 잡혀있기에 홍보관과 소통을 통해 정보를 얻으면 어렵지 않게 지원과 채용이 가능함을 이야기합니다.',
          '단순히 돈을 버는 것 이상의 사회의 존속을 위한 일임을 알려줍니다.',
          '자신의 흥미와 적성을 살릴 수 있는 직무, 창의성을 발휘하고 스스로 성장할 수 있는 환경을 홍보하면 좋습니다.',
          '채용 후 교육시스템이 체계적이기 때문에 빠르게 취업하여 소득생활을 할 수 있음을 이야기합니다.',
          '높은 학력이나 전문적인 기술보다 성실함을 통해 군에서 오래 근무할 수 있으며, 군의 장기 복무 혜택을 받을 수 있는 인재를 선호함을 이야기합니다.',
          '복무 시간 외에 여가시간을 활용하여 자기 계발이나 취미 활동을 활발하게 하는 사례를 이야기합니다.',
          '해외 국방 교류 참여 등 해외 경험의 기회가 많음을 이야기합니다.',
          '군에 취업 후 급여를 받으며 근무하면서 직무적 기초역량을 다지고, 사회로 연계될 수 있는 고용 복지 체계가 갖춰져 있을을 알려줍니다.']
          const shuffled = shuffleArray(candidates, seed); // ✅ 시드 기반으로 섞음
          const picked = shuffled.slice(0, 3); // 3개만 뽑기
          setStrategies(picked);
      }
      setLoadingStrategy(false);
    }, 2000);
  };

  useEffect(() => {
    let currentStrategyIndex = 0;
    let currentCharIndex = 0;
    const tempStrategies = Array(strategies.length).fill(''); // 빈 문자열 배열
  
    const interval = setInterval(() => {
      if (currentStrategyIndex >= strategies.length) {
        clearInterval(interval);
        return;
      }
  
      const fullText = strategies[currentStrategyIndex];
  
      tempStrategies[currentStrategyIndex] += fullText[currentCharIndex];
      setDisplayedStrategies([...tempStrategies]); // 배열 복사해서 반영
  
      currentCharIndex++;
  
      if (currentCharIndex === fullText.length) {
        currentStrategyIndex++;
        currentCharIndex = 0;
      }
    }, 50); // 글자당 50ms
  
    return () => clearInterval(interval);
  }, [strategies]);

  return (
    <AIRecommendationContainer>
      <TitleContainer>
          <ArrowBackIcon sx={{ fontSize:'24px', position: 'absolute', display:'flex', left: 20 }} onClick={()=>onBackClick(false)}/>
        <DialogTitle sx={{fontSize:16, fontWeight:700, padding:'11px'}}>홍보 AI</DialogTitle>
      </TitleContainer>
      <ContentWrapper fromMain={fromMain ?? false}>
      <span style={{fontSize:'18px', fontWeight:600}}>
        홍보 AI가 {selectedSchool?.school_name} 학생에게&nbsp;
          {(selectedSchool?.school_class.includes('대학교') || selectedSchool?.school_type === '특성화고등학교') ?
          '적합한 병과와 홍보 전략을'
          :
          '홍보 키워드와 전략을'
        }
        &nbsp;찾아드립니다.
      </span>
      <RecommendationBox centered={true}>
        <Typography fontSize={16} color='rgba(2, 76, 97, 1)' fontWeight={'bold'}>
          {(selectedSchool?.school_class.includes('대학교') || selectedSchool?.school_type === '특성화고등학교') ?
          'AI가 추천하는 병과'
          :
          'AI가 추천하는 홍보 키워드'
          }
        </Typography>
        {loadingKeywords ? (
          <div style={{marginTop:'24px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:'16px'}}>
            <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(-360deg); }
              }
            `}
            </style>
            <Icon_Loading style={{ animation: 'spin 1s linear infinite', width:'30px',height:'30px' }}/>
            <span style={{color:'#024C61', fontSize:'14px'}}>AI-Powered<br/>Search</span>
          </div>
        ) : (
          <>
            {keywords.length === 0 && (
              <SearchPromotionButton onClick={()=>handleKeywordSearch(selectedSchool?.school_id || 1)} >
                {(selectedSchool?.school_class.includes('대학교') || selectedSchool?.school_type === '특성화고등학교') ?
                  '추천 병과 탐색'
                  :
                  '홍보 키워드 탐색'
                }
              </SearchPromotionButton>
            )}
            {keywords.length > 0 && (
              <AIResultWrapper>
                {keywords.map((keyword, index) => (
                  <FadeInDiv key={index} delay={index * 0.2}>
                  <KeywordBox key={index} bgColor={[
                    '#35B0E6', 
                    '#5DC0EB', 
                    '#86D0F0', 
                    '#AEDFF5'][index % 4]}>
                    <NumberCircleKeyword>{index + 1}</NumberCircleKeyword>
                    <Typography fontSize={'14px'} fontWeight={'bold'}>{keyword}</Typography>
                  </KeywordBox>
                  </FadeInDiv>
                ))}
                
              </AIResultWrapper>
            )}
          </>
        )}
      </RecommendationBox>

      <RecommendationBox centered={strategies.length === 0}>
        <span style={{fontSize:'16px',color: '#024C61', fontWeight:'bold'}}>
          AI가 알려주는 홍보 전략
        </span>
        {loadingStrategy ? (
          <div style={{marginTop:'24px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:'16px'}}>
            <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(-360deg); }
              }
            `}
            </style>
            <Icon_Loading style={{ animation: 'spin 1s linear infinite', width:'30px',height:'30px' }}/>
            <span style={{color:'#024C61', fontSize:'14px'}}>AI-Powered<br/>Search</span>
          </div>
        ) : (
          <>
            {strategies.length === 0 && (
              <SearchPromotionButton onClick={()=>handleStrategySearch(selectedSchool?.school_id || 1, selectedSchool?.school_type.includes('고등학교') || false)}           >
                전략 탐색
              </SearchPromotionButton>
            )}
            {strategies.length > 0 && (
              <StrategyWrapper>
                {displayedStrategies.map((strategy, index) => (
                  strategy.length > 0 && ( // 글자가 하나라도 입력된 줄만 렌더링
                    <div>
                    <Box key={index} display="flex" alignItems="center" flexWrap="nowrap" sx={{borderBottom: index !== displayedStrategies.length-1 ? '1px solid #F4F4F4': 'none', paddingBottom:'10px'}}>
                      <NumberCircleStrategy>{index + 1}</NumberCircleStrategy> {/* 이때 같이 등장 */}
                      <Typography
                        sx={{
                          // fontWeight: "",
                          fontSize: "12px",
                          textAlign: "left",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          flex: 1,
                        }}
                      >
                      {strategy}
                      </Typography>
                    </Box>
                    </div>
                  ) 
                ))}
              </StrategyWrapper>
            )}
          </>
        )}
      </RecommendationBox>
      </ContentWrapper>
    </AIRecommendationContainer>
  );
};

export default AIRecommendation;
